import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, Trash2, Edit, Eye, Globe, Phone, Mail, ArrowLeft, Upload, Star } from 'lucide-react';
import Swal from 'sweetalert2';
import axios from 'axios';

// Create axios instance without interceptors
const api = axios.create({
  baseURL: 'https://backend.dlapakistan.com/api/dla-trade',
  headers: {
    'Content-Type': 'application/json',
  },
});

const AffiliateMembers = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentAffiliate, setCurrentAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [featuredCount, setFeaturedCount] = useState(0);
  
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    imageFile: null,
    website: '',
    phone: '',
    email: '',
    featured: false
  });

  // Fetch affiliates from backend
  const fetchAffiliates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/affiliate-members/');
      setAffiliates(response.data);
      
      // Count featured affiliates
      const featured = response.data.filter(a => a.featured).length;
      setFeaturedCount(featured);
    } catch (error) {
      console.error('Error fetching affiliates:', error);
      Swal.fire('Error', 'Failed to load affiliates. Please check if the backend server is running.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load affiliates on mount
  useEffect(() => {
    fetchAffiliates();
  }, []);

  // Function to convert image to base64 for API
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        Swal.fire('Error', 'Please select an image file', 'error');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        Swal.fire('Error', 'Image size should be less than 2MB', 'error');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);

      setFormData(prev => ({ ...prev, imageFile: file }));
    }
  };

  // Toggle featured status via API
  const toggleFeatured = async (affiliateId) => {
    try {
      const response = await api.post(
        `/affiliate-members/${affiliateId}/toggle_featured/`
      );
      
      // Update local state
      const updatedAffiliates = affiliates.map(a => 
        a.id === affiliateId ? response.data : a
      );
      setAffiliates(updatedAffiliates);
      
      // Update featured count
      const featured = updatedAffiliates.filter(a => a.featured).length;
      setFeaturedCount(featured);
      
      // Update current affiliate if in view mode
      if (isViewMode && currentAffiliate?.id === affiliateId) {
        setCurrentAffiliate(response.data);
      }
      
    } catch (error) {
      if (error.response?.data?.detail) {
        Swal.fire('Error', error.response.data.detail, 'error');
      } else {
        Swal.fire('Error', 'Failed to update featured status', 'error');
      }
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Remove image
  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, imageFile: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Group affiliates by first letter
  const groupedAffiliates = affiliates.reduce((groups, affiliate) => {
    const firstLetter = affiliate.title?.charAt(0).toUpperCase() || 'A';
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(affiliate);
    return groups;
  }, {});

  // Sort letters alphabetically
  const sortedLetters = Object.keys(groupedAffiliates).sort();

  // Handle form changes
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Open add modal
  const handleAdd = () => {
    setIsEditMode(false);
    setCurrentAffiliate(null);
    setFormData({
      title: '',
      imageFile: null,
      website: '',
      phone: '',
      email: '',
      featured: false
    });
    setImagePreview(null);
    setIsModalOpen(true);
  };

  // Open edit modal
  const handleEdit = (affiliate) => {
    setIsEditMode(true);
    setCurrentAffiliate(affiliate);
    setFormData({
      title: affiliate.title || '',
      imageFile: null,
      website: affiliate.website || '',
      phone: affiliate.phone || '',
      email: affiliate.email || '',
      featured: affiliate.featured || false
    });
    setImagePreview(affiliate.image_url || null);
    setIsModalOpen(true);
  };

  // View affiliate details
  const handleView = (affiliate) => {
    setCurrentAffiliate(affiliate);
    setIsViewMode(true);
  };

  // Save affiliate to backend
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let imageBase64 = null;
      
      if (formData.imageFile) {
        imageBase64 = await convertToBase64(formData.imageFile);
      } else if (isEditMode && imagePreview && !imagePreview.includes('http')) {
        // Only use imagePreview if it's a base64 string, not a URL
        imageBase64 = imagePreview;
      }

      const dataToSend = {
        title: formData.title,
        website: formData.website || '',
        phone: formData.phone || '',
        email: formData.email || '',
        featured: formData.featured
      };

      if (imageBase64 && imageBase64.startsWith('data:image')) {
        dataToSend.image_base64 = imageBase64;
      } else if (isEditMode && !imagePreview && currentAffiliate?.image_url) {
        // If removing image in edit mode
        dataToSend.image_base64 = '';
      }

      let response;
      if (isEditMode) {
        response = await api.put(
          `/affiliate-members/${currentAffiliate.id}/`,
          dataToSend
        );
      } else {
        response = await api.post(
          `/affiliate-members/`,
          dataToSend
        );
      }

      setIsModalOpen(false);
      await fetchAffiliates(); // Refresh the list
      
      Swal.fire({
        title: 'Success!',
        text: isEditMode ? 'Affiliate updated!' : 'Affiliate added!',
        icon: 'success',
        timer: 1500
      });
    } catch (error) {
      if (error.response?.data) {
        // Handle validation errors
        if (error.response.data.featured) {
          Swal.fire('Error', error.response.data.featured, 'error');
        } else if (error.response.data.detail) {
          Swal.fire('Error', error.response.data.detail, 'error');
        } else {
          Swal.fire('Error', Object.values(error.response.data).join(', '), 'error');
        }
      } else {
        Swal.fire('Error', 'Failed to save affiliate', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete affiliate from backend
  const handleDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This affiliate will be deleted permanently',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/affiliate-members/${id}/`);
          
          // Update local state
          const updated = affiliates.filter(a => a.id !== id);
          setAffiliates(updated);
          
          // Update featured count
          const featured = updated.filter(a => a.featured).length;
          setFeaturedCount(featured);
          
          if (isViewMode && currentAffiliate?.id === id) {
            setIsViewMode(false);
            setCurrentAffiliate(null);
          }
          
          Swal.fire('Deleted!', 'Affiliate has been deleted.', 'success');
        } catch (error) {
          Swal.fire('Error', 'Failed to delete affiliate', 'error');
        }
      }
    });
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentAffiliate(null);
    setImagePreview(null);
  };

  // Close view
  const closeView = () => {
    setIsViewMode(false);
    setCurrentAffiliate(null);
  };

  const maxFeatured = 3;

  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      <div className="py-10 px-4 sm:px-6 lg:px-8 max-w-8xl mx-auto">
      

        {/* Initial loading state */}
        {loading && affiliates.length === 0 ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mb-4"></div>
              <p className="text-gray-600">Loading affiliates...</p>
            </div>
          </div>
        ) : (
          <>
            {/* HEADER - Only show when NOT in view mode */}
            {!isViewMode && (
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-12">
                <div className="flex-1">
                  <h1 className="text-4xl font-bold text-green-700 mb-4">Affiliate Members</h1>
                  <hr className="border-t-2 border-green-600 mb-6 w-24" />
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Browse our affiliate members from A to Z. Click "Read More" to view complete details.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 lg:w-auto">
                  <button
                    onClick={handleAdd}
                    className="flex items-center px-8 py-3 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold hover:from-green-700 hover:to-emerald-700 shadow-lg transition-all duration-200"
                  >
                    <Plus className="w-5 h-5 mr-2" /> Add Affiliate
                  </button>
                </div>
              </div>
            )}

            {/* VIEW MODE - Simple details page */}
            {isViewMode && currentAffiliate && (
              <div className="mb-8">
                {/* Back button */}
                <button
                  onClick={closeView}
                  className="inline-flex items-center text-black-600 hover:text-green-800 mb-6 transition-colors duration-200 group"
                >
                  <ArrowLeft className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" />
                </button>

                {/* Simple Details Card */}
                <div className="bg-white rounded-2xl shadow-sm">
                  {/* Header */}
                  <div className="px-8 py-6 border-b border-gray-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold text-gray-900">{currentAffiliate.title}</h1>
                        {currentAffiliate.featured && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-sm font-medium">
                            <Star className="w-4 h-4 fill-yellow-500" />
                            Featured
                          </span>
                        )}
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleEdit(currentAffiliate)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                          title="Edit affiliate"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(currentAffiliate.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          title="Delete affiliate"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Simple Content Layout */}
                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left - Image */}
                      <div>
                        <div className="w-full h-64 bg-gray-50 rounded-xl overflow-hidden mb-6">
                          {currentAffiliate.image_url ? (
                            <div className="w-full h-full flex items-center justify-center p-4">
                              <img
                                src={currentAffiliate.image_url}
                                alt={currentAffiliate.title}
                                className="max-w-full max-h-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="text-center">
                                <div className="w-20 h-20 mx-auto mb-3 bg-gray-200 rounded-full flex items-center justify-center">
                                  <span className="text-3xl font-bold text-gray-500">
                                    {currentAffiliate.title?.charAt(0) || 'A'}
                                  </span>
                                </div>
                                <p className="text-gray-400">No Image</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right - Simple Details List */}
                      <div className="space-y-6">
                        {/* Featured Status */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-yellow-500" />
                            <h3 className="font-medium text-gray-700">Featured Status</h3>
                          </div>
                          <div className="flex items-center gap-3">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={currentAffiliate.featured || false}
                                onChange={() => toggleFeatured(currentAffiliate.id)}
                                className="sr-only peer"
                              />
                              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                              <span className="ml-3 text-sm font-medium text-gray-900">
                                {currentAffiliate.featured ? 'Featured' : 'Not Featured'}
                              </span>
                            </label>
                            <span className="text-sm text-gray-500">
                              {currentAffiliate.featured ? '✓ This affiliate is featured' : 'Click to feature this affiliate'}
                            </span>
                          </div>
                        </div>

                        {/* Website */}
                        {currentAffiliate.website && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-green-600" />
                              <h3 className="font-medium text-gray-700">Website</h3>
                            </div>
                            <a
                              href={currentAffiliate.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-700 hover:text-green-900 break-all block"
                            >
                              {currentAffiliate.website}
                            </a>
                          </div>
                        )}

                        {/* Phone */}
                        {currentAffiliate.phone && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-green-600" />
                              <h3 className="font-medium text-gray-700">Service Number</h3>
                            </div>
                            <div className="flex items-center gap-3">
                              <p className="text-gray-800">{currentAffiliate.phone}</p>
                              <a
                                href={`tel:${currentAffiliate.phone}`}
                                className="text-sm text-green-600 hover:text-green-800"
                              >
                                Call
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Email */}
                        {currentAffiliate.email && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-green-600" />
                              <h3 className="font-medium text-gray-700">Service Email</h3>
                            </div>
                            <div className="flex items-center gap-3">
                              <a
                                href={`mailto:${currentAffiliate.email}`}
                                className="text-gray-800 hover:text-green-700"
                              >
                                {currentAffiliate.email}
                              </a>
                              <a
                                href={`mailto:${currentAffiliate.email}`}
                                className="text-sm text-green-600 hover:text-green-800"
                              >
                                Email
                              </a>
                            </div>
                          </div>
                        )}

                        {/* ID */}
                        <div className="pt-4 border-t border-gray-100">
                          <p className="text-sm text-gray-500">
                            ID: <span className="font-medium">{currentAffiliate.id}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* MAIN CONTENT - Clean card grid */}
            {!isViewMode && (
              <>
                {/* Alphabetical Sections - Clean design */}
                {sortedLetters.length > 0 ? (
                  <div className="space-y-8">
                    {sortedLetters.map(letter => (
                      <div key={letter} className="space-y-4">
                        {/* Simple letter header */}
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <span className="text-xl font-bold text-green-700">{letter}</span>
                          </div>
                          <h2 className="text-xl font-semibold text-gray-800">
                            Affiliates starting with "{letter}"
                          </h2>
                        </div>
                        
                        {/* Clean card grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {groupedAffiliates[letter].map(affiliate => (
                            <div
                              key={affiliate.id}
                              className={`bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden relative ${
                                affiliate.featured ? 'ring-2 ring-yellow-400' : ''
                              }`}
                            >
                              {/* Featured Badge */}
                              {affiliate.featured && (
                                <div className="absolute top-2 left-2 z-10">
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500 text-white text-xs font-semibold rounded-md">
                                    <Star className="w-3 h-3 fill-white" />
                                    Featured
                                  </span>
                                </div>
                              )}

                              {/* Clean image container */}
                              <div className="w-full h-40 bg-gray-50">
                                <div className="w-full h-full flex items-center justify-center p-2">
                                  {affiliate.image_url ? (
                                    <img
                                      src={affiliate.image_url}
                                      alt={affiliate.title}
                                      className="max-w-full max-h-full object-contain"
                                    />
                                  ) : (
                                    <div className="text-center">
                                      <div className="w-16 h-16 mx-auto mb-2 bg-gray-200 rounded-full flex items-center justify-center">
                                        <span className="text-2xl font-bold text-gray-500">
                                          {affiliate.title?.charAt(0) || 'A'}
                                        </span>
                                      </div>
                                      <p className="text-gray-400 text-sm">No Image</p>
                                    </div>
                                  )}
                                </div>

                                {/* Featured toggle button */}
                                <button
                                  onClick={() => toggleFeatured(affiliate.id)}
                                  className={`absolute top-2 right-2 p-1.5 rounded-full z-10 ${
                                    affiliate.featured 
                                      ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200' 
                                      : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-yellow-500'
                                  } transition-colors duration-200`}
                                  title={affiliate.featured ? "Unfeature this affiliate" : "Feature this affiliate"}
                                >
                                  <Star className={`w-4 h-4 ${affiliate.featured ? 'fill-yellow-500' : ''}`} />
                                </button>
                              </div>
                              
                              {/* Clean content */}
                              <div className="p-3">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2 truncate">
                                  {affiliate.title}
                                </h3>
                                
                                {/* Simple action buttons */}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleView(affiliate)}
                                    className="flex-1 bg-green-600 text-white px-3 py-1.5 rounded-md hover:bg-green-700 transition-colors duration-200 text-sm font-medium"
                                  >
                                    Read More
                                  </button>
                                  
                                  <button
                                    onClick={() => handleEdit(affiliate)}
                                    className="px-2 text-gray-600 hover:text-green-600 transition-colors duration-200"
                                    title="Edit affiliate"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Simple empty state */
                  <div className="text-center py-16">
                    <div className="max-w-md mx-auto">
                      <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                        <Plus className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Affiliate Members Yet</h3>
                      <p className="text-gray-600 mb-6">
                        Start by adding your first affiliate member.
                      </p>
                      <button
                        onClick={handleAdd}
                        className="inline-flex items-center px-6 py-2.5 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition-colors duration-200"
                      >
                        <Plus className="w-4 h-4 mr-2" /> Add Your First Affiliate
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ADD/EDIT MODAL - Clean design */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditMode ? 'Edit Affiliate' : 'Add Affiliate'}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 text-gray-500 hover:text-gray-700 rounded transition-colors duration-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="p-6 space-y-5">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                    placeholder="Enter affiliate title"
                  />
                </div>

                {/* Featured Checkbox */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleFormChange}
                      disabled={loading || (!isEditMode && featuredCount >= maxFeatured)}
                      className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <Star className={`w-5 h-5 ${formData.featured ? 'text-yellow-500 fill-yellow-500' : 'text-gray-400'}`} />
                        <span className="font-medium text-gray-900">Feature this affiliate</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {formData.featured 
                          ? 'This affiliate will be highlighted as featured.' 
                          : 'Feature this affiliate to highlight it on your site.'}
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        {featuredCount < maxFeatured 
                          ? `${maxFeatured - featuredCount} of ${maxFeatured} featured slots available`
                          : 'Maximum featured affiliates reached'}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image (Optional)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={loading}
                  />
                  
                  {imagePreview ? (
                    <div className="space-y-3">
                      <div className="w-full h-40 bg-gray-50 rounded-lg overflow-hidden">
                        <div className="w-full h-full flex items-center justify-center p-4">
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={triggerFileInput}
                          disabled={loading}
                          className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 text-sm"
                        >
                          Change
                        </button>
                        <button
                          type="button"
                          onClick={removeImage}
                          disabled={loading}
                          className="flex-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 disabled:opacity-50 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={triggerFileInput}
                      className={`border border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-600 text-sm">Click to upload image</p>
                      <p className="text-xs text-gray-400 mt-1">Max 2MB • JPG, PNG, WebP</p>
                    </div>
                  )}
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website URL (Optional)
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleFormChange}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                    placeholder="https://example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Number (Optional)
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleFormChange}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                    placeholder="+1-800-555-1234"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Email (Optional)
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                    placeholder="contact@example.com"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={loading}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !formData.title.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors duration-200 flex items-center gap-2"
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  {isEditMode ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AffiliateMembers;